// A task group's costs, all time (GET /api/task-groups/{id}/costs): the total, tokens and steps,
// and whose model key paid.
import { Coins } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

import { groupCostsQuery } from "../../costs/api";
import { compact, type CostReport, money, tokens } from "../../costs/model";
import { PaidSplitBar } from "../../costs/PaidSplitBar";
import { useT } from "../../i18n";
import { CollapsibleCard } from "../../ui/CollapsibleCard";
import { ErrorState, Loading } from "../../ui/states";
import { Chip, Sparkline, SummaryRow } from "../../ui/summary";
import { costsSummary, TREND_DAYS } from "./summaries";

/** `now` is when the report was fetched, so the series ends on the day it describes. */
function Summary({ report, now }: { report: CostReport; now: number }) {
  const c = useT().tasks.cards.costs;
  const s = costsSummary(report, now);
  return (
    <SummaryRow>
      <Chip title={c.total(money(s.total))}>{money(s.total)}</Chip>
      <Sparkline values={s.trend} label={c.trend(TREND_DAYS, money(s.today))} />
    </SummaryRow>
  );
}

export function GroupCostsCard({ groupId }: { groupId: string }) {
  const t = useT().tasks.group;
  const query = useQuery(groupCostsQuery(groupId));
  const r = query.data;
  return (
    <CollapsibleCard
      kind="costs"
      icon={<Coins weight="duotone" />}
      title={t.costs}
      description={t.costsDescription}
      summary={r ? <Summary report={r} now={query.dataUpdatedAt} /> : null}
    >
      {query.isPending ? <Loading /> : null}
      {query.isError ? <ErrorState message={query.error.message} /> : null}
      {r ? (
        <>
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="tabular font-display text-3xl font-extrabold text-gray-900">{money(r.total_usd)}</div>
              <div className="text-xs text-gray-500">{t.steps(r.steps)}</div>
            </div>
            <div className="text-end">
              <div className="tabular text-lg font-bold text-gray-900">{compact(tokens(r))}</div>
              <div className="text-xs text-gray-500">{t.tokens}</div>
            </div>
          </div>
          <PaidSplitBar className="mt-4" report={r} />
        </>
      ) : null}
    </CollapsibleCard>
  );
}
